import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import {
  Search,
  RefreshCw,
  Users,
  FileText,
  Calendar,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';

import RecordDetails from '../components/RecordDetails.jsx';
import Header from '../components/Header.jsx';
import ScheduleCalendar from '../components/ScheduleCalendar.jsx';
import Pagination from '../components/Pagination.jsx';
import {
  fetchProviderRecords,
  selectProviderError,
  selectProviderLimit,
  selectProviderLoading,
  selectProviderPage,
  selectProviderRecords,
  selectProviderSearch,
  selectProviderSelectedRecord,
  selectProviderTotal,
  selectProviderViewMode,
  setLimit,
  setPage,
  setSearch,
  setSelectedRecord,
  setViewMode,
  updateProviderRecord,
} from '../store/providerSlice.js';

/**
 * Provider Dashboard
 * Shows only the patients (records) where record.provider === loggedInUser.fullName (or username)
 */
const ProviderDashboard = () => {
  const { user, logout } = useAuth();
  const dispatch = useDispatch();

  const records = useSelector(selectProviderRecords);
  const total = useSelector(selectProviderTotal);
  const page = useSelector(selectProviderPage);
  const limit = useSelector(selectProviderLimit);
  const loading = useSelector(selectProviderLoading);
  const error = useSelector(selectProviderError);
  const search = useSelector(selectProviderSearch);
  const selectedRecord = useSelector(selectProviderSelectedRecord);
  const viewMode = useSelector(selectProviderViewMode);

  useEffect(() => {
    if (!user) return;
    if (viewMode !== 'records') return;

    dispatch(fetchProviderRecords());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, page, limit, search, viewMode]);

  const handleSaveRecord = async (id, data) => {
    try {
      const updated = await dispatch(updateProviderRecord({ id, data })).unwrap();
      dispatch(setSelectedRecord(updated));
      return updated;
    } catch (err) {
      console.error('Failed to update provider record', err);
      throw err;
    }
  };

  const handleBackFromDetail = () => {
    dispatch(setSelectedRecord(null));
    if (viewMode === 'records') {
      dispatch(fetchProviderRecords());
    }
    // if from schedule, we don't need to re-fetch here
  };

  const handleUpdateUser = (updatedUser) => {
    console.log('User updated:', updatedUser);
  };

  if (!user) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading user data...
      </div>
    );
  }

  const filterName = user.fullName || user.username || 'Unknown';

  const navItems = [
    { label: 'My Patients', icon: <Users className="w-4 h-4" />, mode: 'records' },
    { label: 'Schedule', icon: <Calendar className="w-4 h-4" />, mode: 'schedule' },
  ];

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header
        user={user}
        onLogout={logout}
        api={api}
        onUpdateUser={handleUpdateUser}
        navItems={navItems}
        currentViewMode={viewMode}
        onViewModeChange={(mode) => dispatch(setViewMode(mode))}
      />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        {/* When a record is selected (from list OR calendar), show details */}
        {selectedRecord ? (
          <RecordDetails
            record={selectedRecord}
            user={user}
            api={api}
            onBack={handleBackFromDetail}
            onSave={handleSaveRecord}
            onAssign={null}      // hide collector assignment for provider
            collectors={[]}
          />
        ) : (
          <>
            {viewMode === 'records' && (
              <>
                <div className="mb-8">
                  <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                    <Users className="w-8 h-8 text-indigo-600" />
                    My Patients
                  </h1>
                  <p className="mt-2 text-gray-600">
                    Viewing records for Provider:{' '}
                    <span className="font-semibold text-indigo-600">
                      {filterName}
                    </span>
                  </p>
                </div>

                {/* Search + refresh */}
                <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4">
                  <div className="relative w-full sm:max-w-md">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500 sm:text-sm"
                      placeholder="Search by Patient Name, Case Status..."
                      value={search}
                      onChange={(e) => {
                        dispatch(setSearch(e.target.value));
                        dispatch(setPage(1));
                      }}
                    />
                  </div>
                  <button
                    onClick={() => dispatch(fetchProviderRecords())}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    <RefreshCw
                      className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`}
                    />
                    Refresh
                  </button>
                </div>

                {/* List / states */}
                {error && (
                  <div className="bg-red-50 text-red-700 p-4 rounded-md flex items-center gap-2 mb-4">
                    <AlertCircle className="w-5 h-5" />
                    {error}
                  </div>
                )}

                {loading ? (
                  <div className="flex justify-center py-12">
                    <RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" />
                  </div>
                ) : records.length === 0 ? (
                  <div className="bg-white rounded-lg shadow p-12 text-center">
                    <Users className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">
                      No patients found
                    </h3>
                    <p className="mt-1 text-sm text-gray-500">
                      No records match provider "<strong>{filterName}</strong>".
                    </p>
                  </div>
                ) : (
                  <>
                    <div className="bg-white shadow overflow-hidden sm:rounded-md">
                      <ul className="divide-y divide-gray-200">
                        {records.map((record) => (
                          <li key={record._id}>
                            <button
                              type="button"
                              onClick={() => dispatch(setSelectedRecord(record))}
                              className="block w-full text-left hover:bg-gray-50 cursor-pointer transition duration-150 ease-in-out"
                            >
                              <div className="px-4 py-4 sm:px-6">
                                <div className="flex items-center justify-between">
                                  <div className="flex flex-col">
                                    <p className="text-lg font-semibold text-indigo-600 truncate">
                                      {record.ptName}
                                    </p>
                                    <p className="text-sm text-gray-500">
                                      DOB:{' '}
                                      {record.dob
                                        ? new Date(
                                            record.dob
                                          ).toLocaleDateString()
                                        : 'N/A'}
                                    </p>
                                  </div>
                                  <div className="ml-2 flex-shrink-0 flex">
                                    <span
                                      className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                      ${
                                        record.caseStatus === 'SETTLED'
                                          ? 'bg-green-100 text-green-800'
                                          : record.caseStatus === 'CIC PENDING'
                                          ? 'bg-yellow-100 text-yellow-800'
                                          : 'bg-gray-100 text-gray-800'
                                      }`}
                                    >
                                      {record.caseStatus || 'No Status'}
                                    </span>
                                  </div>
                                </div>
                                <div className="mt-2 sm:flex sm:justify-between">
                                  <div className="sm:flex sm:space-x-6">
                                    <p className="flex items-center text-sm text-gray-500">
                                      <FileText className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      Claim #: {record.claimNo?.[0]?.value || 'N/A'}
                                    </p>
                                    <p className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                      <Calendar className="flex-shrink-0 mr-1.5 h-4 w-4 text-gray-400" />
                                      Hearing:{' '}
                                      {record.hearingDate
                                        ? new Date(
                                            record.hearingDate
                                          ).toLocaleDateString()
                                        : 'None'}
                                    </p>
                                  </div>
                                  <div className="mt-2 flex items-center text-sm text-gray-500 sm:mt-0">
                                    <CheckCircle
                                      className={`flex-shrink-0 mr-1.5 h-4 w-4 ${
                                        record.outstanding <= 0
                                          ? 'text-green-500'
                                          : 'text-red-400'
                                      }`}
                                    />
                                    Balance: $
                                    {record.outstanding?.toLocaleString() ||
                                      '0.00'}
                                  </div>
                                </div>
                              </div>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <Pagination
                      page={page}
                      setPage={(value) => dispatch(setPage(value))}
                      limit={limit}
                      setLimit={(value) => dispatch(setLimit(value))}
                      total={total}
                    />
                  </>
                )}
              </>
            )}

            {viewMode === 'schedule' && (
              <div className="mt-4">
                <h2 className="text-2xl font-semibold text-gray-900 flex items-center gap-2 mb-4">
                  <Calendar className="w-6 h-6 text-indigo-600" />
                  My Schedule
                </h2>

                {/* IMPORTANT: pass onRecordSelect, so events can open RecordDetails */}
                <ScheduleCalendar
                  user={user}
                  api={api}
                  onRecordSelect={(record) => dispatch(setSelectedRecord(record))}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ProviderDashboard;
